import api from '@shared/services/api';
import { CreateTaskData, GroupedTasks, Task, TasksResponse, TaskStatus, UpdateTaskData } from '../types/task.types';

/**
 * Service for handling task-related API calls
 */
export const TaskService = {
  /**
   * Get all tasks for the authenticated user
   */
  async getTasks(): Promise<GroupedTasks> {
    const response = await api.get<TasksResponse>('/tasks');
    return response.data.tasks;
  },

  /**
   * Create a new task
   */
  async createTask(data: CreateTaskData): Promise<Task> {
    // Ensure the date is sent in UTC
    const taskData = {
      ...data,
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : undefined
    };
    const response = await api.post<{ task: Task }>('/tasks', taskData);
    return response.data.task;
  },

  /**
   * Update an existing task
   */
  async updateTask(taskId: string, data: UpdateTaskData): Promise<Task> {
    // Ensure the date is sent in UTC
    const taskData = {
      ...data,
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : undefined
    };
    const response = await api.put<{ task: Task }>(`/tasks/${taskId}`, taskData);
    return response.data.task;
  },

  /**
   * Delete a task
   */
  async deleteTask(taskId: string): Promise<void> {
    await api.delete(`/tasks/${taskId}`);
  },

  /**
   * Reorder tasks
   */
  async reorderTask(taskId: string, newPosition: number): Promise<void> {
    await api.post('/tasks/reorder', { taskId, newPosition });
  },

  /**
   * Add a subtask to a task
   */
  async addSubtask(taskId: string, title: string): Promise<Task> {
    const response = await api.post<{ task: Task }>(`/tasks/${taskId}/subtasks`, { title });
    return response.data.task;
  },

  /**
   * Toggle a subtask's completion status
   */
  async toggleSubtask(taskId: string, subtaskId: string): Promise<Task> {
    const response = await api.post<{ task: Task }>(`/tasks/${taskId}/subtasks/${subtaskId}/toggle`);
    return response.data.task;
  },

  /**
   * Update positions of tasks
   */
  async updatePositions(status: TaskStatus, taskIds: string[]): Promise<void> {
    await api.post("/tasks/positions", {
      status,
      taskIds,
    });
  }
};
